import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Layout, Menu, Avatar, Dropdown, Space, Typography, Tag } from "antd";
import {
  DashboardOutlined,
  FileTextOutlined,
  QuestionCircleOutlined,
  ApartmentOutlined,
  AppstoreOutlined,
  PictureOutlined,
  TeamOutlined,
  ExperimentOutlined,
  AuditOutlined,
  SettingOutlined,
  LogoutOutlined,
  UserOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useAuth } from "@/stores/auth";
import { http } from "@/api/client";

const { Header, Sider, Content } = Layout;

const menuItems = [
  { key: "/", icon: <DashboardOutlined />, label: "仪表盘" },
  {
    key: "library-group",
    icon: <AppstoreOutlined />,
    label: "教学库管理",
    children: [
      { key: "/categories", icon: <AppstoreOutlined />, label: "分类管理" },
      { key: "/cases", icon: <FileTextOutlined />, label: "病例管理" },
    ],
  },
  { key: "/questions", icon: <QuestionCircleOutlined />, label: "题库管理" },
  { key: "/levels", icon: <ApartmentOutlined />, label: "关卡管理" },
  {
    key: "/media-lab",
    icon: <ExperimentOutlined />,
    label: "媒体工具（媒体试听）",
  },
  { key: "/faqs", icon: <QuestionCircleOutlined />, label: "常见问题" },
  { key: "/members", icon: <TeamOutlined />, label: "会员管理" },
  { key: "/audit-log", icon: <AuditOutlined />, label: "审计日志" },
  { key: "/settings", icon: <SettingOutlined />, label: "全局设置" },
  { type: "divider" as const },
  { key: "/home-recs", icon: <PictureOutlined />, label: "首页推荐" },
  { key: "/review", icon: <CheckCircleOutlined />, label: "审核工作台" },
];

export default function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, clear } = useAuth();

  const handleLogout = async () => {
    try {
      await http.post("/auth/logout");
    } catch {
      // 即使后端失败也清前端
    }
    clear();
    localStorage.removeItem("admin_access_token");
    localStorage.removeItem("heartsound-admin-auth");
    navigate("/login");
  };

  // 选中 key：最长前缀匹配（兼容子菜单）
  const collectKeys = (items: typeof menuItems): string[] =>
    items.flatMap((m) => [
      ...(m.key && m.key !== "library-group" ? [m.key as string] : []),
      ...(m.children ? collectKeys(m.children) : []),
    ]);
  const allKeys = collectKeys(menuItems);
  const selectedKey =
    allKeys
      .filter((k) => location.pathname === k || location.pathname.startsWith(k + "/"))
      .sort((a, b) => b.length - a.length)[0] ?? "/";

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        theme="dark"
        width={220}
        trigger={null}
        breakpoint="lg"
        collapsible
      >
        <div
          style={{
            color: "#fff",
            fontSize: 18,
            fontWeight: 600,
            padding: "20px 24px",
            letterSpacing: 1,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          🩺 心聆管理后台
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
        />
      </Sider>
      <Layout>
        <Header
          style={{
            background: "#fff",
            padding: "0 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <Typography.Text type="secondary">
            V1.0 · 内容生产端 · <Tag color="blue">ADMIN</Tag>
          </Typography.Text>
          <Dropdown
            menu={{
              items: [
                {
                  key: "logout",
                  icon: <LogoutOutlined />,
                  label: "退出登录",
                  onClick: handleLogout,
                },
              ],
            }}
            placement="bottomRight"
          >
            <Space style={{ cursor: "pointer" }}>
              <Avatar icon={<UserOutlined />} />
              <span>{user?.nickname ?? "未登录"}</span>
            </Space>
          </Dropdown>
        </Header>
        <Content style={{ padding: 24, background: "#f5f5f5" }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
